import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { stateIsLoaded, stateIsLoading, widgetGetIntegration } from '@guryou/redux';
import {
  WIDGET_AUTH_BRANDING_SNAPSHOT_KEY,
  WIDGET_AUTH_INTEGRATION_QUERY_KEY,
  WIDGET_AUTH_INTEGRATION_STORAGE_KEY,
  WIDGET_AUTH_RETURN_URL_QUERY_KEY,
  normalizeWidgetIntegrationAccent,
  readWidgetAuthBrandingFromSession,
  writeWidgetAuthReturnUrlToSession,
} from '../constants/widgetIntegrationBranding';
import { useFeelPanelBranding } from './FeelPanelBrandingContext';
import { ensureWidgetIntegrationGoogleFont } from '../utils/widgetIntegrationGoogleFonts';

function WidgetIntegrationBrandingSync() {
  const location = useLocation();
  const history = useHistory();
  const dispatch = useDispatch();
  const { setPanelBranding } = useFeelPanelBranding();
  const auth = useSelector(s => s.auth);
  const integrationState = useSelector(s => s.widget.integration);
  const lastAppliedIdRef = useRef(null);

  const isLoggedIn = stateIsLoaded(auth) && !!auth?.data;

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const returnTo = params.get(WIDGET_AUTH_RETURN_URL_QUERY_KEY);
    if (returnTo) {
      writeWidgetAuthReturnUrlToSession(returnTo);
      params.delete(WIDGET_AUTH_RETURN_URL_QUERY_KEY);
    }
    if (!params.has(WIDGET_AUTH_INTEGRATION_QUERY_KEY)) {
      if (returnTo) {
        const q = params.toString();
        history.replace({ pathname: location.pathname, search: q ? `?${q}` : '' });
      }
      return;
    }
    const id = params.get(WIDGET_AUTH_INTEGRATION_QUERY_KEY);
    try {
      sessionStorage.setItem(WIDGET_AUTH_INTEGRATION_STORAGE_KEY, id);
    } catch (_) {
      /* ignore */
    }
    params.delete(WIDGET_AUTH_INTEGRATION_QUERY_KEY);
    const q = params.toString();
    history.replace({ pathname: location.pathname, search: q ? `?${q}` : '' });
  }, [location.search, location.pathname, history]);

  useEffect(() => {
    const isProviderRoute = /^\/(en|it)\/provider\/\d+/.test(location.pathname);
    if (isProviderRoute) {
      return;
    }
    if (!isLoggedIn) {
      lastAppliedIdRef.current = null;
      document.body.style.fontFamily = '';
      document.body.style.fontStyle = '';
      document.body.style.fontWeight = '';
      return;
    }

    let idStr = null;
    try {
      idStr = sessionStorage.getItem(WIDGET_AUTH_INTEGRATION_STORAGE_KEY);
    } catch (_) {
      return;
    }
    if (!idStr) {
      return;
    }
    const idNum = parseInt(idStr, 10);
    if (Number.isNaN(idNum)) {
      return;
    }

    if (stateIsLoading(integrationState)) {
      return;
    }

    const currentId = integrationState.data?.id;
    if (currentId !== idNum) {
      dispatch(widgetGetIntegration(idNum));
    }
  }, [isLoggedIn, dispatch, integrationState, location.pathname]);

  useEffect(() => {
    const isProviderOrBookRoute = /^\/(en|it)\/provider\/\d+/.test(location.pathname) || location.pathname.startsWith('/book');
    if (isProviderOrBookRoute) {
      return;
    }
    if (!isLoggedIn) {
      return;
    }

    let idStr = null;
    try {
      idStr = sessionStorage.getItem(WIDGET_AUTH_INTEGRATION_STORAGE_KEY);
    } catch (_) {
      return;
    }
    if (!idStr) {
      return;
    }
    const idNum = parseInt(idStr, 10);
    if (Number.isNaN(idNum) || !integrationState.data || integrationState.data.id !== idNum) {
      return;
    }

    if (lastAppliedIdRef.current === idNum) {
      return;
    }
    lastAppliedIdRef.current = idNum;

    const cfg = integrationState.data.configuration || {};
    const provider = integrationState.data.provider;
    const logoUrl = provider?.images?.[0]?.medium || provider?.images?.[0]?.large || provider?.images?.[0]?.xLarge || null;
    const brandName = provider?.name || integrationState.data.name || '';
    let accent = normalizeWidgetIntegrationAccent(cfg.primaryColor);
    if (!accent) {
      try {
        const raw = sessionStorage.getItem(WIDGET_AUTH_BRANDING_SNAPSHOT_KEY);
        const snap = raw ? JSON.parse(raw) : null;
        if (snap && String(snap.integrationId) === String(idNum)) {
          accent = normalizeWidgetIntegrationAccent(snap.primaryColor);
        }
      } catch (_) {
        /* ignore */
      }
    }
    const fromSession = readWidgetAuthBrandingFromSession();
    const merged = {
      logoUrl: logoUrl || fromSession?.logoUrl || null,
      brandName: brandName || fromSession?.brandName || '',
      accentColor: accent || fromSession?.accentColor || null,
    };
    if (merged.logoUrl || merged.brandName || merged.accentColor) {
      setPanelBranding(merged);
    }

    const fontF = cfg.fontFamily;
    if (fontF && fontF !== 'Metropolis') {
      ensureWidgetIntegrationGoogleFont(fontF);
      document.body.style.fontFamily = `'${fontF}', sans-serif`;
    } else {
      document.body.style.fontFamily = '';
    }

    const fontS = cfg.fontStyle;
    if (fontS === 'italic') {
      document.body.style.fontStyle = 'italic';
      document.body.style.fontWeight = '';
    } else if (fontS === 'bold' || fontS === 'Double-struck') {
      document.body.style.fontStyle = '';
      document.body.style.fontWeight = '700';
    } else {
      document.body.style.fontStyle = '';
      document.body.style.fontWeight = '';
    }
  }, [isLoggedIn, integrationState.data, setPanelBranding, location.pathname]);

  return null;
}

export default WidgetIntegrationBrandingSync;
